import { FC, useId } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PaymentPlanDto } from 'src/api/paymentPlanService/dto/PaymentPlanDto';
import { Select } from 'src/components/select';
import { Currency } from 'src/shared/enum/Currency';
import { getCurrencySymbol } from 'src/utils/get-currency-symbol';
import { createLabelFromInstalmentType } from 'src/utils/instalment-type-to-name-convert';

import { styles } from './styles';

interface InstalmentSelectorProps {
    paymentPlans: Record<string, PaymentPlanDto>;
    value: string;
    currency: Currency;
    onChange: (instalmentType: string) => void;
}

export const InstalmentSelector: FC<InstalmentSelectorProps> = ({
    paymentPlans,
    value,
    currency,
    onChange,
}) => {
    const { t } = useTranslation();
    const currencySymbol = getCurrencySymbol(currency);
    const labelId = useId();

    const getInstalmentLabel = (instalmentType: string) => {
        try {
            return createLabelFromInstalmentType(instalmentType, t);
        } catch {
            return instalmentType;
        }
    };

    return (
        <Box css={styles.instalmentSelector}>
            <Typography variant="h4" id={labelId}>
                {t('booking_summary_booking_details_instalment_plan_subtitle')}
            </Typography>
            <Box css={styles.instalmentOptions} role="radiogroup" aria-labelledby={labelId}>
                {Object.entries(paymentPlans).map(([instalmentType, paymentPlan], index) => (
                    <Select
                        key={instalmentType}
                        value={instalmentType}
                        isSelected={instalmentType === value}
                        onClick={() => onChange(instalmentType)}
                        title={`${currencySymbol}${paymentPlan.weeklyPrice.toFixed(2)}${t('booking_summary_per_week_payment')}`}
                        subtitle={getInstalmentLabel(instalmentType)}
                        id={`view-room-instalment-option-${index + 1}`}
                        name="view-room-instalment-plan"
                    />
                ))}
            </Box>
        </Box>
    );
};
